import { createPage } from 'client/pages'
import { usePage } from 'client/store/index'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import { EntityUnionType } from 'common/types/entities/EntityInterface'
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
} from 'react'
import { useTranslation } from 'react-i18next'
import { LibraryElementEnum } from 'src/shared/enums/LibraryElementEnum'
import { isObject } from 'src/shared/utils/is-object'
import { getEntityCustomName } from '../utils/get-entity-custom-name'

type GetEntityCustomName = (
  libraryElementType: LibraryElementEnum,
  pageType: PageTypeEnum,
) => string | undefined

type GetEntityCustomNameContextValue = {
  getEntityCustomName: GetEntityCustomName
}

const GetEntityCustomNameContext =
  createContext<GetEntityCustomNameContextValue>({
    getEntityCustomName: () => undefined,
  })

const useGetEntityCustomName = () => {
  const { t } = useTranslation()
  const page = usePage(page => page)

  const getEntityCustomNameCallback = useCallback(
    (libraryElementType: LibraryElementEnum, pageType: PageTypeEnum) => {
      const { createLibraryElement } = createPage(pageType)
      const entity = createLibraryElement(libraryElementType)

      if (isObject<EntityUnionType>(entity) && 'customName' in entity) {
        return getEntityCustomName({
          entityType: entity.type as EntityTypeEnum,
          entities: page.entities,
          t,
        })
      }
    },
    [page.entities, t],
  )

  return getEntityCustomNameCallback
}

export const useGetEntityCustomNameContext = () =>
  useContext(GetEntityCustomNameContext)

export const GetEntityCustomNameProvider = ({
  children,
}: PropsWithChildren) => {
  const getEntityCustomName = useGetEntityCustomName()
  return (
    <GetEntityCustomNameContext.Provider value={{ getEntityCustomName }}>
      {children}
    </GetEntityCustomNameContext.Provider>
  )
}
